import React from "react";
import { AlertTriangle, X, Package } from "lucide-react";
import type { StockItem } from "../../types";

interface LowStockAlertProps {
  lowStockItems: StockItem[];
  onClose: () => void;
  onNavigateToStock: () => void;
}

export function LowStockAlert({ lowStockItems, onClose, onNavigateToStock }: LowStockAlertProps) {
  if (lowStockItems.length === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-300"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-slate-800/95 dark:backdrop-blur-xl border border-gray-200/80 dark:border-white/[0.08] rounded-3xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — premium, sans gradient */}
        <div className="p-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                Alerte stock bas
              </h3>
              <p className="text-[15px] text-gray-500 dark:text-slate-400 mt-0.5">
                {lowStockItems.length} article{lowStockItems.length > 1 ? "s" : ""} sous le seuil
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200"
            title="Fermer (jusqu'au prochain rechargement)"
          >
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 space-y-4">
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
            {lowStockItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 rounded-2xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06]"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400">
                    <Package className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-[15px] truncate">
                      {item.name}
                    </p>
                    <p className="text-[13px] text-gray-500 dark:text-slate-400">
                      Seuil : {item.lowStockThreshold ?? 10}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-xl font-semibold text-red-600 dark:text-red-400 tabular-nums">
                    {item.quantity}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    restant{item.quantity > 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              onClick={onNavigateToStock}
              className="flex-1 py-3.5 px-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium rounded-2xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <Package size={18} strokeWidth={1.75} />
              Gérer le stock
            </button>
            <button
              onClick={onClose}
              className="px-5 py-3.5 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-slate-300 font-medium rounded-2xl hover:bg-gray-200 dark:hover:bg-white/10 transition-colors text-[15px]"
            >
              Plus tard
            </button>
          </div>

          <p className="text-[12px] text-center text-gray-400 dark:text-slate-500">
            Cette alerte réapparaîtra au prochain rechargement.
          </p>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 3px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
        }
      `}</style>
    </div>
  );
}
